import React from 'react';
import { Card, Skeleton } from 'antd';
import {
  TransactionOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  RollbackOutlined,
  DollarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { spacing } from '../../../design-system/tokens';

const formatVnd = (v: number): string =>
  new Intl.NumberFormat('vi-VN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v) + ' đ';

const ICON_MAP: Record<string, React.ReactNode> = {
  total: <TransactionOutlined style={{ fontSize: 20, color: '#1890ff' }} />,
  success: <CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} />,
  failed: <CloseCircleOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />,
  refunded: <RollbackOutlined style={{ fontSize: 20, color: '#fa8c16' }} />,
  volume: <DollarOutlined style={{ fontSize: 20, color: '#722ed1' }} />,
  pending: <ClockCircleOutlined style={{ fontSize: 20, color: '#faad14' }} />,
};

interface TransactionLogSummaryCardProps {
  type: 'total' | 'success' | 'failed' | 'refunded' | 'volume' | 'pending';
  title: string;
  value: number | string;
  subtitle: string;
  loading?: boolean;
}

export const TransactionLogSummaryCard: React.FC<
  TransactionLogSummaryCardProps
> = ({ type, title, value, subtitle, loading }) => {
  if (loading) {
    return (
      <Card style={{ height: '100%' }}>
        <Skeleton.Input active size="small" style={{ width: '60%' }} />
        <Skeleton.Input active style={{ width: '80%', marginTop: 8 }} />
      </Card>
    );
  }

  return (
    <Card
      size="small"
      style={{
        height: '100%',
        borderRadius: 12,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
      bodyStyle={{ padding: spacing.md }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              color: '#8c8c8c',
              marginBottom: 4,
              fontWeight: 500,
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: 24,
              fontWeight: 600,
              color: '#262626',
              lineHeight: 1.2,
            }}
          >
            {value}
          </div>
          <div
            style={{
              fontSize: 11,
              color: '#bfbfbf',
              marginTop: 6,
            }}
          >
            {subtitle}
          </div>
        </div>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {ICON_MAP[type]}
        </div>
      </div>
    </Card>
  );
};

export const formatSummaryValue = (
  type: 'total' | 'success' | 'failed' | 'refunded' | 'volume' | 'pending',
  value: number
): string => {
  if (type === 'volume') return formatVnd(value);
  return new Intl.NumberFormat('vi-VN').format(value);
};
